import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import vendorService from "../../services/vendor.service";

// ... (fetchVendorDashboard, fetchVendorMachines, fetchVendorOrders same rahenge)

export const fetchVendorDashboard = createAsyncThunk(
  "vendor/fetchDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const response = await vendorService.getDashboard();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch dashboard" }
      );
    }
  }
);

export const fetchVendorMachines = createAsyncThunk(
  "vendor/fetchMachines",
  async (params, { rejectWithValue }) => {
    try {
      const response = await vendorService.getMyMachines(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch machines" }
      );
    }
  }
);

export const fetchVendorOrders = createAsyncThunk(
  "vendor/fetchOrders",
  async (params, { rejectWithValue }) => {
    try {
      const response = await vendorService.getMyOrders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch orders" }
      );
    }
  }
);

export const createMachine = createAsyncThunk(
  "vendor/createMachine",
  async (machineData, { rejectWithValue }) => {
    try {
      const response = await vendorService.createMachine(machineData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create machine" }
      );
    }
  }
);

// ✅ Added Update Thunk
export const updateVendorMachine = createAsyncThunk(
  "vendor/updateMachine",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await vendorService.updateMachine(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update machine" }
      );
    }
  }
);

// ✅ Added Delete Thunk (Missing in your code)
export const deleteVendorMachine = createAsyncThunk(
  "vendor/deleteMachine",
  async (id, { rejectWithValue }) => {
    try {
      await vendorService.deleteMachine(id);
      return id; // Return ID so we can remove it from state
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete machine" }
      );
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "vendor/updateOrderStatus",
  async ({ orderId, action, data }, { rejectWithValue }) => {
    try {
      const response = await vendorService.updateOrderStatus(
        orderId,
        action,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update order" }
      );
    }
  }
);

const vendorSlice = createSlice({
  name: "vendor",
  initialState: {
    dashboard: null,
    machines: [],
    orders: [],
    pagination: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchVendorDashboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVendorDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchVendorDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Machines Fetch
      .addCase(fetchVendorMachines.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVendorMachines.fulfilled, (state, action) => {
        state.loading = false;
        state.machines = action.payload.machines;
        state.pagination = action.payload.pagination;
      })

      // Orders Fetch
      .addCase(fetchVendorOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVendorOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })

      // Create Machine
      .addCase(createMachine.fulfilled, (state, action) => {
        state.machines.unshift(action.payload.machine);
      })

      // ✅ Update Machine Case
      .addCase(updateVendorMachine.fulfilled, (state, action) => {
        const index = state.machines.findIndex(
          (m) => m._id === action.payload.machine._id
        );
        if (index !== -1) {
          state.machines[index] = action.payload.machine;
        }
      })

      // ✅ Delete Machine Case (UI update without refresh)
      .addCase(deleteVendorMachine.fulfilled, (state, action) => {
        state.machines = state.machines.filter((m) => m._id !== action.payload);
      })

      // Order Status Update
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const index = state.orders.findIndex(
          (o) => o._id === action.payload.booking._id
        );
        if (index !== -1) {
          state.orders[index] = action.payload.booking;
        }
      });
  },
});

export const { clearError } = vendorSlice.actions;
export default vendorSlice.reducer;
